import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, Users, X, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'In attesa', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  confirmed: { label: 'Confermata', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rifiutata', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancellata', color: 'bg-slate-100 text-slate-500', icon: X },
};

const MyLessonsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBookings(); }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lessons/my`, { withCredentials: true });
      setBookings(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Sei sicuro di voler cancellare questa prenotazione?')) return;
    try {
      await axios.delete(`${API_URL}/api/lessons/${bookingId}`, { withCredentials: true });
      toast.success('Prenotazione cancellata');
      fetchBookings();
    } catch (error) {
      toast.error('Errore nella cancellazione');
    }
  };

  const today = startOfDay(new Date());
  const upcoming = bookings.filter(b => !isBefore(parseISO(b.date), today) && b.status !== 'cancelled' && b.status !== 'rejected');
  const past = bookings.filter(b => isBefore(parseISO(b.date), today) || b.status === 'cancelled' || b.status === 'rejected');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="my-lessons-page">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="font-outfit text-2xl font-semibold text-slate-900">Le Mie Lezioni</h1>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Nessuna lezione prenotata</p>
          <p className="text-slate-400 text-sm mt-1">Prenota una lezione con un Maestro dalla Dashboard</p>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Prossime</h2>
              <div className="space-y-3">
                {upcoming.map(b => {
                  const cfg = statusConfig[b.status] || statusConfig.pending;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={b.booking_id} data-testid={`lesson-${b.booking_id}`}
                      className="bg-white rounded-2xl p-4 border border-slate-100">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cfg.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {cfg.label}
                            </span>
                            <span className="text-xs text-slate-400">{b.label} · {b.price}€</span>
                          </div>
                          <p className="font-medium text-slate-900">
                            Lezione con {b.maestro_name || 'Maestro'}
                          </p>
                          <div className="flex items-center gap-3 mt-1.5 text-sm text-slate-500">
                            <span className="flex items-center gap-1 capitalize">
                              <Calendar className="w-3.5 h-3.5" />
                              {format(parseISO(b.date), 'EEE d MMM', { locale: it })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {b.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {b.num_players}
                            </span>
                          </div>
                        </div>
                        {b.status === 'pending' && (
                          <button
                            onClick={() => cancelBooking(b.booking_id)}
                            data-testid={`cancel-${b.booking_id}`}
                            className="w-9 h-9 flex items-center justify-center rounded-xl text-red-400 hover:bg-red-50 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Passate</h2>
              <div className="space-y-2 opacity-60">
                {past.map(b => {
                  const cfg = statusConfig[b.status] || statusConfig.pending;
                  return (
                    <div key={b.booking_id} className="bg-white rounded-2xl p-3 border border-slate-100 flex items-center gap-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                      <span className="text-sm text-slate-600 capitalize">{format(parseISO(b.date), 'EEE d MMM', { locale: it })}</span>
                      <span className="text-sm text-slate-500">{b.time}</span>
                      <span className="text-sm text-slate-400 ml-auto">{b.price}€</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyLessonsPage;
